import { SingleSelectInput } from '@components/inputs/SelectInput/SingleSelectInput';
import clsx from 'clsx';
import { UtilityTypeEnum } from 'enums/UtilityTypeEnum';
import { CardContainer } from 'layout/Card';
import { parseUnitType } from 'utils/formatters/units/getReadableUnitByName';
import { BuildingMeterChart } from '../charts/BuildingMeterChart';
import { BuildingSankeyAlternativeMeterChart } from '../charts/BuildingSankeyAlternativeMeterChart';
import { BuildingSankeyChart } from '../charts/BuildingSankeyChart';
import { BuildingSankeyButton } from './BuildingSankeyButton';
import { SelectedMeterTag } from './SelectedMeterTag';
import { type FC, useEffect, useMemo, useState } from 'react';
import { FEATURE_FLAGS_LIST } from 'lib/featureFlagsList';
import { useFeatureFlag } from 'context/FeatureFlagProvider/FeatureFlagProvider';
import { useSeverity } from 'hooks/contexts/SeverityContext';
import {
  useFetchBuildingSankey,
  useFetchMeterTimeseries,
} from 'api/assets/buildingViewService';
import { type SankeyButtonsData } from 'interfaces/assets/Building.interface';
import BuildingSankeyNonConsumptionButton from './BuildingSankeyButton/BuildingSankeyNonConsumptionButton';
import { useListAssetNotes } from 'api/assets/assetsServices';
import { useTranslation } from 'react-i18next';

const SANKEY_BUTTONS_ORDER = [
  'ELECTRICITY',
  'GAS',
  'WATER',
  'FUEL_OIL',
  'THERMAL_ENERGY',
];
const SELECTED_METER_INITIAL_VALUE = {
  id: undefined,
  name: undefined,
};
const SANKEY_DROPDOWN_OPTIONS = [
  { value: 'year', name: 'Last year' },
  { value: 'month', name: 'Last month' },
];

interface SankeySectionProps {
  id?: string;
  severity?: string;
  onNoteClick?: () => void;
}

const SankeySection: FC<SankeySectionProps> = ({
  id,
  severity,
  onNoteClick,
}) => {
  const { t } = useTranslation();
  const [selectedSankeyDropdownOption, setSelectedSankeyDropdownOption] =
    useState('year');

  const [selectedSankeyOption, setSelectedSankeyOption] = useState(
    UtilityTypeEnum.ELECTRICITY as string
  );
  const { data: meterNotesData } = useListAssetNotes(id);

  const [selectedMeter, setSelectedMeter] = useState<{
    id?: number;
    name?: string;
  }>(SELECTED_METER_INITIAL_VALUE);

  const { hasFeatureFlagPermission } = useFeatureFlag();
  const { getSeverityBorderColor, getSeveritySmileyIcon } = useSeverity();

  const { data: meterTimeseriesData } = useFetchMeterTimeseries(
    selectedMeter?.id
  );

  const { data: buildingChartsData, isFetching: isFetchingSankey } =
    useFetchBuildingSankey(
      id,
      selectedSankeyOption,
      selectedSankeyDropdownOption
    );

  const changesMap = useMemo(() => {
    if (meterTimeseriesData?.changes_map) {
      return Object.entries(meterTimeseriesData.changes_map).map(
        ([id, date]) => ({
          date,
          id,
        })
      );
    }
    return [];
  }, [meterTimeseriesData]);

  const handleSelectMeter = ({
    meterId,
    meterName,
  }: {
    meterId: number;
    meterName: string;
  }): void => {
    if (selectedMeter.id === meterId) {
      setSelectedMeter({ id: undefined, name: undefined });
      return;
    }
    setSelectedMeter({ id: meterId, name: meterName });
  };

  const sortButtons = (a: SankeyButtonsData, b: SankeyButtonsData): number => {
    const indexA = SANKEY_BUTTONS_ORDER.indexOf(a.utility_type);
    const indexB = SANKEY_BUTTONS_ORDER.indexOf(b.utility_type);

    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    if (indexA !== -1) {
      return -1;
    }

    if (indexB !== -1) {
      return 1;
    }

    return a.utility_type.localeCompare(b.utility_type);
  };

  const sortedButtons = useMemo(() => {
    const utilityButtons = buildingChartsData?.utility_types;
    if (utilityButtons) {
      return [...utilityButtons].sort(sortButtons).filter((button) =>
        [
          'ELECTRICITY',
          'GAS',
          'WATER',
          'FUEL_OIL',
          'THERMAL_ENERGY',
          // 'TEMPERATURE',
          // 'HUMIDITY',
          // 'AIR_PRESSURE',
          // 'CARBON_DIOXIDE',
        ].includes(button.utility_type)
      );
    }

    return [];
  }, [buildingChartsData]);

  const nonConsumptionButton = useMemo(() => {
    const utilityButtons = buildingChartsData?.utility_types;
    if (utilityButtons) {
      return [...utilityButtons]
        .sort(sortButtons)
        .filter(
          (button) =>
            ![
              'ELECTRICITY',
              'GAS',
              'WATER',
              'FUEL_OIL',
              'THERMAL_ENERGY',
            ].includes(button.utility_type)
        );
    }
    return [];
  }, [buildingChartsData]);

  useEffect(() => {
    setSelectedMeter(SELECTED_METER_INITIAL_VALUE);
  }, [selectedSankeyOption]);

  // Update selected utility type if the current selection is not available
  useEffect(() => {
    if (buildingChartsData?.utility_types) {
      const availableButtons = buildingChartsData.utility_types
        .filter((button) => !button.is_empty)
        .sort(sortButtons);

      // Check if current selection is available
      const isCurrentSelectionAvailable = availableButtons.some(
        (button) => button.utility_type === selectedSankeyOption
      );

      // If current selection is not available, select the first available one
      if (!isCurrentSelectionAvailable && availableButtons.length > 0) {
        setSelectedSankeyOption(availableButtons[0].utility_type);
      }
    }
  }, [buildingChartsData]); // Remove selectedSankeyOption from dependencies to avoid loop

  return hasFeatureFlagPermission(
    FEATURE_FLAGS_LIST.buildingView.get_sankey
  ) ? (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between items-center">
        <div className="flex gap-2 items-center">
          <h3 className="font-bold uppercase">
            {t('buildingView.energyEvolution.title')}
          </h3>
          {!isFetchingSankey && getSeveritySmileyIcon(severity)}
        </div>
        <div className="flex items-center">
          <SingleSelectInput
            options={SANKEY_DROPDOWN_OPTIONS}
            value={selectedSankeyDropdownOption}
            onChange={(value: string | number | undefined) => {
              setSelectedSankeyDropdownOption(value as string);
            }}
            maxWidth="150px"
            placeholder={t('buildingView.energyEvolution.selectDate')}
            removable={false}
          />
        </div>
      </div>

      <CardContainer
        gap={6}
        className={clsx(!isFetchingSankey && getSeverityBorderColor(severity))}
      >
        <div className="flex gap-2">
          {sortedButtons?.map((button) => {
            if (button.is_empty) return <></>;
            const parsedUtility = button.utility_type.toLowerCase();

            const value = button.energy_use;

            const percentage = button.percentage;

            return (
              <BuildingSankeyButton
                key={parsedUtility}
                id={button.utility_type}
                isSelected={selectedSankeyOption === button.utility_type}
                handleSelection={setSelectedSankeyOption}
                utilityType={
                  UtilityTypeEnum[
                    button.utility_type as keyof typeof UtilityTypeEnum
                  ]
                }
                value={value}
                percentage={percentage}
                unit={parseUnitType(button.unit) ?? ''}
              />
            );
          })}
          {nonConsumptionButton.length > 0 && (
            <BuildingSankeyNonConsumptionButton
              key={'NON_CONSUMPTION'}
              id={'NON_CONSUMPTION'}
              isSelected={selectedSankeyOption === 'NON_CONSUMPTION'}
              handleSelection={setSelectedSankeyOption}
            />
          )}
        </div>
        {buildingChartsData?.use_alternative_chart ? (
          // ALTERNATIVE CHART
          <BuildingSankeyAlternativeMeterChart
            key={selectedSankeyOption}
            data={buildingChartsData?.meters}
            barOnClick={handleSelectMeter}
            utilityType={selectedSankeyOption}
            unit={parseUnitType(buildingChartsData?.sankey?.unit)}
          />
        ) : (
          // SANKEY CHART
          <BuildingSankeyChart
            data={buildingChartsData?.sankey}
            nodeOnClick={handleSelectMeter}
            unit={parseUnitType(buildingChartsData?.sankey?.unit)}
            totalConsumptionEnergy={
              buildingChartsData?.utility_types?.find(
                (ut) => ut.utility_type === selectedSankeyOption
              )?.energy_use
            }
          />
        )}
        {/* METER SELECTED CHART */}
        <SelectedMeterTag meter={selectedMeter} />

        {selectedMeter?.id && (
          <div>
            {/* <ChartButton
              buttonList={buttonList}
              activeChart={activeChart}
              handleActiveChart={setActiveChart}
              isLoading={false}
            /> */}
            {/* {activeChart === CHART_BUTTON_VALUES.MEASURED && ( */}
            <BuildingMeterChart
              data={meterTimeseriesData ? meterTimeseriesData.time_series : []}
              unit={meterTimeseriesData?.unit}
              changesData={changesMap}
              notes={meterNotesData.notes ?? []}
              onNoteClick={onNoteClick}
              isNonConsumption={selectedSankeyOption === 'NON_CONSUMPTION'}
            />
            {/* )} */}
            {/* {activeChart === CHART_BUTTON_VALUES.CUSUM && (

            {/* {activeChart === CHART_BUTTON_VALUES.MEASURED && ( */}
            {/* <BuildingMeterChart
              data={meterTimeseriesData ? meterTimeseriesData.time_series : []}
              changesData={changesMap}
              unit={meterTimeseriesData?.unit}
            /> */}
            {/* )} */}
            {/* {activeChart === CHART_BUTTON_VALUES.CUSUM && (
              <ExcessConsumptionCuSUMChart
                data={
                  meterTimeseriesData ? meterTimeseriesData.time_series : []
                }
                unit=" "
              />
            )} */}
          </div>
        )}
      </CardContainer>
    </div>
  ) : (
    <></>
  );
};
export default SankeySection;
